import { z } from 'zod';
import { IdSchema, TimestampSchema, MoneySchema } from './common.js';

/**
 * Handsets, dialling lists and cost-centre groupings.
 *
 * A DEVICE is separate from an extension because one extension may be
 * reachable on several devices (desk phone plus mobile app), and a handset gets
 * re-assigned between people without the extension changing.
 */

export const DeviceVendorSchema = z.enum([
  'yealink', 'grandstream', 'fanvil', 'polycom', 'snom', 'cisco', 'generic',
]);

export const DeviceLineSchema = z.object({
  id: IdSchema,
  extension_id: IdSchema,
  extension_number: z.string(),
  extension_name: z.string(),
  line_number: z.number().int(),
  label: z.string().nullable(),
});

export const DeviceSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  mac_address: z.string().nullable(),
  vendor: DeviceVendorSchema,
  model: z.string().nullable(),
  label: z.string().nullable(),
  firmware_target: z.string().nullable(),
  status: z.enum(['unprovisioned', 'provisioned', 'online', 'offline', 'retired']),
  provisioned_at: TimestampSchema.nullable(),
  last_seen_at: TimestampSchema.nullable(),
  lines: z.array(DeviceLineSchema).default([]),
  created_at: TimestampSchema,
});
export type Device = z.infer<typeof DeviceSchema>;

/**
 * MAC accepted in any common notation. The database column normalises it, so
 * 'AA:BB:CC:DD:EE:FF' and 'aabbccddeeff' cannot both exist as separate rows for
 * one physical phone.
 */
const MAC_RE = /^(?:[0-9A-Fa-f]{2}[:-]?){5}[0-9A-Fa-f]{2}$/;

export const DeviceUpsertSchema = z.object({
  mac_address: z.string().regex(MAC_RE, 'must be a 12-digit MAC address').nullable().optional(),
  vendor: DeviceVendorSchema.default('generic'),
  model: z.string().max(64).nullable().optional(),
  label: z.string().max(120).nullable().optional(),
  site_id: IdSchema.nullable().optional(),
  lines: z.array(z.object({
    extension_id: IdSchema,
    line_number: z.number().int().min(1).max(24).default(1),
    label: z.string().max(64).optional(),
  })).default([]),
}).strict();

export const DialListKindSchema = z.enum(['block', 'allow', 'country']);

export const DialListSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema.nullable(),
  name: z.string(),
  kind: DialListKindSchema,
  patterns: z.array(z.string()),
  description: z.string().nullable(),
  /** Platform lists are shared and read-only to a tenant. */
  is_platform_default: z.boolean().optional(),
});
export type DialList = z.infer<typeof DialListSchema>;

export const DialListUpsertSchema = z.object({
  name: z.string().min(1).max(80),
  kind: DialListKindSchema,
  // Digits, optionally ending in % as a wildcard: '27861%' is all SA premium.
  patterns: z.array(z.string().regex(/^\d{1,15}%?$/, 'digits, optionally ending in %')).default([]),
  description: z.string().max(300).nullable().optional(),
}).strict();

export const AdminGroupSchema = z.object({
  id: IdSchema,
  name: z.string(),
  code: z.string().nullable(),
  extension_count: z.number().int().optional(),
});

export const AdminGroupUpsertSchema = z.object({
  name: z.string().min(1).max(80),
  code: z.string().max(24).nullable().optional(),
}).strict();

export const VoicemailBoxSchema = z.object({
  id: IdSchema,
  extension_id: IdSchema.nullable(),
  mailbox_number: z.string(),
  name: z.string().nullable(),
  email_to: z.array(z.string()).nullable(),
  email_attach: z.boolean(),
  delete_after_email: z.boolean(),
  transcribe: z.boolean(),
  max_message_sec: z.number().int(),
  greeting_mode: z.enum(['default', 'recorded', 'tts']),
  greeting_text: z.string().nullable(),
  has_pin: z.boolean(),
});
export type VoicemailBox = z.infer<typeof VoicemailBoxSchema>;

export const VoicemailUpsertSchema = z.object({
  mailbox_number: z.string().regex(/^\d{2,6}$/),
  name: z.string().max(120).nullable().optional(),
  email_to: z.array(z.string().email()).max(10).default([]),
  email_attach: z.boolean().default(true),
  delete_after_email: z.boolean().default(false),
  transcribe: z.boolean().default(false),
  max_message_sec: z.number().int().min(30).max(1800).default(300),
  greeting_mode: z.enum(['default', 'recorded', 'tts']).default('default'),
  greeting_text: z.string().max(600).nullable().optional(),
  /** Cleared by sending null; never returned. */
  pin: z.string().regex(/^\d{4,10}$/).nullable().optional(),
}).strict();

export const BudgetPeriodSchema = z.enum(['daily', 'weekly', 'monthly']);
export const RecordModeSchema = z.enum(['none', 'all', 'inbound', 'outbound', 'on_demand']);
export const PhoneEngineSchema = z.enum(['external', 'webrtc', 'mobile', 'softphone']);
export const BudgetAmountSchema = MoneySchema;

/**
 * Account-wide recording policy.
 *
 * `default_mode` is what a NEW extension inherits; it never rewrites existing
 * seats. Retention is the part with legal weight (doc 22) and applies to the
 * whole account, because a per-seat retention would make "delete everything
 * older than N" unanswerable.
 */
export const RecordingPolicySchema = z.object({
  tenant_id: IdSchema,
  default_mode: z.enum(['none', 'all', 'inbound', 'outbound', 'on_demand']),
  retention_days: z.number().int().nullable(),
  announce_mode: z.enum(['none', 'inbound', 'outbound', 'both']),
  allow_pause: z.boolean(),
  storage_quota_mb: z.number().int().nullable(),
  updated_at: TimestampSchema.nullable(),
});
export type RecordingPolicy = z.infer<typeof RecordingPolicySchema>;

export const RecordingPolicyUpsertSchema = z.object({
  default_mode: z.enum(['none', 'all', 'inbound', 'outbound', 'on_demand']).default('none'),
  retention_days: z.number().int().min(1).max(3650).nullable().optional(),
  announce_mode: z.enum(['none', 'inbound', 'outbound', 'both']).default('none'),
  allow_pause: z.boolean().default(false),
  storage_quota_mb: z.number().int().min(1).nullable().optional(),
}).strict();

/** Applies one recording mode to a named set of extensions, in one request. */
export const RecordingBulkSchema = z.object({
  extension_ids: z.array(IdSchema).min(1).max(500),
  record_mode: z.enum(['none', 'all', 'inbound', 'outbound', 'on_demand']),
}).strict();
